import { defineStore } from "pinia";
import { Group } from "@/common/models/group";
import { Player } from "@/common/models/player";
import { fetchGroupPlayers } from "@/common/services/players.service";
import { useErrorStore } from "@/common/store/error.store";
import { useClassificationsStore } from "@/common/store/classifications.store";
import { useBetsStore } from "@/common/store/bets.store";
import { removeEntity, upsertEntities, removeByProp } from "@/common/store/utils";
import { Classification } from "@/common/models/classification";

interface PlayersState {
  ids: string[];
  entities: { [id: string]: Player };
}

export const usePlayersStore = defineStore("players", {
  state: (): PlayersState => ({
    ids: [],
    entities: {},
  }),

  getters: {
    all(): Player[] {
      const classificationsStore = useClassificationsStore();
      const betsStore = useBetsStore();
      const players = this.ids
        .map((id) => this.entities[id])
        .map((player) => {
          const classification = classificationsStore.all.find(
            (c) => player && c.player === player.id,
          );
          const bets = betsStore.all.filter((b) => player && b.player === player.id);
          return {
            ...player,
            classification: classification ? classification : (null as unknown as Classification),
            bets,
          };
        });
      return players;
    },
  },

  actions: {
    async fetchGroupPlayers(group: Group): Promise<void> {
      const classificationsStore = useClassificationsStore();
      try {
        const players = await fetchGroupPlayers(group);
        await classificationsStore.fetchGroupClassifications(group);
        this.fetchGroupPlayersSuccess(group, players);
      } catch (error) {
        this.fetchGroupPlayersError(error);
      }
    },

    fetchGroupPlayersSuccess(group: Group, players: Player[]): void {
      removeByProp("group", group.id, this);
      upsertEntities(players, this);
    },

    fetchGroupPlayersError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchGroupPlayersError: ${err.toString()}`);
    },

    upsertPlayer(player: Player): void {
      upsertEntities([player], this);
    },

    removePlayer(player: Player): void {
      removeEntity(player, this);
    },
  },
});
